import type { ReactNode } from 'react';
import { useCallback } from 'react';

import type { CheckboxOption } from 'components/Checkboxes/CheckboxOption';
import { Checkboxes } from 'components/Checkboxes/Checkboxes';

import { Field } from './Field/Field';

type Props = {
  columns?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  disabled?: boolean;
  fullWidth?: boolean;
  name: string;
  label?: ReactNode;
  description?: string;
  options: CheckboxOption[] | readonly CheckboxOption[];
  large?: boolean;
  onUpdateValue?: (value: string[]) => void;
  transformOnChange?: (names: string[]) => string[];
};

/*
 * A field that contains multiple checkboxes. Outputs values as `{ fieldName: [OPTION_NAME] }`.
 */
export function MultiCheckboxField({
  columns,
  disabled,
  fullWidth,
  name,
  label,
  description,
  large,
  onUpdateValue,
  transformOnChange,
  ...props
}: Props) {
  const getNewValue = useCallback(
    ({ value = [], option }: { value?: string[]; option: string }) => {
      const newValue = value.includes(option)
        ? value.filter((o) => o !== option)
        : [...value, option];

      return transformOnChange ? transformOnChange(newValue) : newValue;
    },
    [transformOnChange],
  );

  return (
    <Field
      inputProps={props}
      name={name}
      label={label}
      description={description}
      renderInput={({ value = [], id, qaId, hasError, updateValue }) => (
        <Checkboxes
          columns={columns}
          disabled={disabled}
          fullWidth={fullWidth}
          id={id}
          getValue={(optionName) => value.includes(optionName)}
          hasError={hasError}
          onChange={(option) => {
            const newValue = getNewValue({ value, option });

            updateValue(newValue);

            if (onUpdateValue) {
              onUpdateValue(newValue);
            }
          }}
          data-qa-id={qaId}
          large={large}
          getQaId={(checkboxName) => checkboxName}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      )}
    />
  );
}
